import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import { useParams } from 'react-router-dom';
import './LoanList.scss';
import DropdownBlue from '../../../components/Dropdowns/Dropdown/Dropdown';
import LoanDetails from 'scenes/Private/LoanDetails/LoanDetails';
import { ILoanListItemProps, LoanListItem, LoanStatus } from './components/LoanItemList';
import { useGetLoans } from '../../../services/loan/querys';
import { LoanDetailContext } from '../Private';
import Spin from '../../../components/Atoms/Loader/Spin';
import { Pagination } from 'antd';
import { useGetLoanById } from '../../../services/loan/querys';

/* Loan List */
const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
    <rect width="30" height="30" rx="6" transform="matrix(0 -1 -1 0 30 30)" fill="#F9FAFB" />
    <path d="M14.1667 20.8333C17.8486 20.8333 20.8333 17.8486 20.8333 14.1667C20.8333 10.4848 17.8486 7.5 14.1667 7.5C10.4848 7.5 7.5 10.4848 7.5 14.1667C7.5 17.8486 10.4848 20.8333 14.1667 20.8333Z" stroke="#929FB0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M22.5005 22.5003L18.8755 18.8753" stroke="#929FB0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

export const OpenTaskContext = createContext<{ taskKey: string; setTaskKey: any } | {}>({});

interface ILoanListProps {
  children?: React.ReactNode;
}

const LoanList: React.FC<ILoanListProps> = ({ }) => {
  const navigate = useNavigate();
  const { loanId } = useParams();
  const { loan: loanSelected, setLoan: setLoanSelected }: any = useContext(LoanDetailContext);

  /* Badges */
  const [selectedStatus, setSelectedStatus] = useState<LoanStatus | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loanStateFilter, setLoanStateFilter] = useState<'Active' | 'Closed' | 'Waiting' | 'Dead'>('Active');

  const LOAN_STATE_LABELS: Record<string, string> = {
    'Active': 'Active',
    'Closed': 'Closed',
    'Waiting': 'Waiting',
    'Dead': 'Archived'
  };

  const { 
    data: loans, 
    total: totalLoans, 
    totalAll = 0, 
    totalReview = 0, 
    totalApproved = 0, 
    totalPages,
    fetching: loansLoading, 
    reexecute: getLoans 
  } = useGetLoans({
    search: searchTerm,
    page: currentPage,
    limit: pageSize,
    status: selectedStatus ?? undefined,
    state: loanStateFilter,
  });

  const counts = {
    all: totalAll,
    review: totalReview,
    approval: totalApproved,
  };

  const badgesMarkup = (
    <React.Fragment>
      <StatusBadge
        title="All"
        count={counts.all}
        isSelected={!selectedStatus}
        onClick={() => onStatusChange(null)}
      />
      <StatusBadge
        title="Needs Review"
        count={counts.review}
        isSelected={selectedStatus === 'review'}
        onClick={() => onStatusChange('review')}
      />
      <StatusBadge
        title="Final Approval"
        count={counts.approval}
        isSelected={selectedStatus === 'approval'}
        onClick={() => onStatusChange('approval')}
      />
    </React.Fragment>
  )

  const [openTask, setOpenTask] = useState<string>();

  const { data: loanById, fetching: fetchingLoanById } = useGetLoanById(
    loanId ? { id: loanId } : { id: '' },
    undefined
  );

  useEffect(() => {
    if (!loanId) return;

    if (loanById) {
      setLoanSelected(loanById);
    } else {
      let selected = loans?.find((loan: any) => loan._id === loanId);
      if (selected) {
        setLoanSelected(selected);
      }
    }
  }, [loanId, loans, loanById]);

  const onSearchHandler = useCallback((term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  }, []);

  const onStatusChange = (status: LoanStatus | null) => {
    setSelectedStatus(status);
    setCurrentPage(1);
  };

  const onStateFilterChange = (newState: 'Active' | 'Closed' | 'Waiting' | 'Dead') => {
    setLoanStateFilter(newState);
    setCurrentPage(1);
  };

  const redirectLoan = (loan: any) => {
    navigate(`/loans/${loan._id}`);
    setLoanSelected(loan);
  }


  const displayLoans = Array.isArray(loans) ? loans : [];

  const sortedLoans = displayLoans.sort((a: any, b: any) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <OpenTaskContext.Provider value={{ taskKey: openTask, setTaskKey: setOpenTask }}>
      <div className="loan-list">
        <div className='top-container'>
          <div className='title'>
            <DropdownBlue 
              items={[
                { key: 'Active', label: "Active" },
                { key: 'Closed', label: "Closed" },
                { key: 'Waiting', label: "Waiting" },
                { key: 'Dead', label: "Archived" }
              ]}
              label={LOAN_STATE_LABELS[loanStateFilter]}
              onClickSelect={onStateFilterChange}
            /> loans
          </div>
          <div>
            <SearchBox onSearch={onSearchHandler} />
          </div>
        </div>
        <div className='badges-container'>
          {badgesMarkup}
        </div>
        <div className='loans-container'>
          {loansLoading && <Spin />}
          { !loansLoading &&
            sortedLoans?.map((loan: any, index: any) => (
              <LoanListItem key={index} {...loan} onClick={() => redirectLoan(loan)} />
            ))
          }
        </div>
        {!loansLoading && totalLoans > 0 && (
          <div style={{ textAlign: 'center', marginTop: '16px'}}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalPages * pageSize}
              onChange={(page, newPageSize) => {
                setCurrentPage(page);
                setPageSize(newPageSize);
              }}
              showSizeChanger
              pageSizeOptions={['10', '20', '50', '100']}
              />
          </div>
        )}
      </div>
      {
        loanSelected ?
          <div className='floating-window'>
            <div className='floating-overlay' onClick={() => {
              setLoanSelected(undefined);
              navigate('/loans')
            }}></div>
            <div className='floating-content'>
              <LoanDetails onClose={() => {
                  setLoanSelected(undefined);
                  navigate('/loans');
                }}
                onLoanClosed={getLoans} />
            </div>
          </div>
          :
          null
      }
    </OpenTaskContext.Provider>
  );
};

/* Search Box */
interface ISearchBoxProps {
  onSearch: (searchTerm: string) => void;
}

export const SearchBox: React.FC<ISearchBoxProps> = ({ onSearch }) => {
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      onSearch(searchTerm ?? '');
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, onSearch]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  return (
    <div className='search-box'>
      <SearchIcon />
      <input
        type="text"
        placeholder="Filter by borrower or loan address"
        value={searchTerm ?? ''}
        onChange={handleSearch}
      />
    </div>
  )
}

/* Status Badge */
interface IStatusBadgeProps {
  title: string;
  count?: number;
  isSelected?: boolean;
  children?: React.ReactNode;
  onClick?: () => void;
}

export const StatusBadge: React.FC<IStatusBadgeProps> = (props) => {
  const { title, count, isSelected, onClick } = props;

  const className = isSelected ? 'status-badge selected' : 'status-badge';
  const countClassName = count !== undefined  && count === 0 ? 'zero' : '';

  return (
    <div className={`${className} ${countClassName}`} onClick={onClick}>
      <span className='title'>{title}</span>
      {count !== undefined ?
        <React.Fragment>
          {' '}
          <span className='count'>{count}</span>
        </React.Fragment>
        :
        null
      }
    </div>
  )
}

/* Exports */
export default LoanList;